import { useState, useEffect, useMemo } from 'react';
import { LinearProgress, Tabs } from '../../tailwindcomponents';
import EditProperties from './EditProperties';
import AllCertificates from './AllCerfiticates';
import WaitingForApproval from './WaitingForApproval';
import History from './History';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducer';
import EditCompanies from './EditCompanies';
import EditSubcontracts from './Edit Subcontracts';
import LicenseTable from './LicenseTable';
import CertificatesApi from '../../api/certificates';
import { License, CertificateAircraftCategory, CertificateRating } from '../../types/certificates';
import { useQueryTab } from '../../utils/usePersistedTab';
import SkillManagement from './SkillManagement';

function Certificates() {
  const store = useSelector((state: RootState) => state);
  const [userType, setUserType] = useState<string>('view');
  const userGroups = store.user.me.user_clearence_groups;
  const userId = store.user.me.user_id;
  const { tabValue, setTabValue } = useQueryTab("tab", 0);
  const [loading, setLoading] = useState<boolean>(true);
  const certificatesLicenseTableView = userGroups.includes('Licenses Edit') || userGroups.includes('Licenses View');

  const [ratings, setRatings] = useState<CertificateRating[]>([]);
  const [categories, setCategories] = useState<CertificateAircraftCategory[]>([]);
  const [licenses, setLicenses] = useState<License[]>([]);

  useEffect(() => {
    if (userGroups !== undefined) {
      if (userGroups.includes("Certificates Create") || userGroups.includes("Certificates Approver")) {
        setUserType('CertAdmin');
      } else {
        setUserType("view");
      }
    }
    Promise.all([
      CertificatesApi.GetAllCertificationRatings(),
      CertificatesApi.GetAllCertififcateAircraftCategories(),
      CertificatesApi.GetAllLicenses(),
    ]).then(([rRes, cRes, xRes]) => {
      setRatings(rRes.data.filter(r => r.visible));
      setCategories(cRes.data.filter(c => c.visible));
      setLicenses(xRes.data);
    }).finally(() => setLoading(false));
  }, [userGroups, userId]);

  const changeTab = (tabValue: number) => {
    setTabValue(tabValue);
  }

  /**
 * Map like { "A320FAM-CSB1": ["AM,MC", "AM,MC,EE"], … }
 */
  const catByRatingString = useMemo(() => {
    const ratingNameById = Object.fromEntries(ratings.map(r => [r.id, r.rating]));
    const categoryNameById = Object.fromEntries(categories.map(c => [c.id, c.aircraft_category]));

    const map: Record<string, string[]> = {};

    licenses.forEach(({ rating_id, category_id }) => {
      const ratingName = ratingNameById[rating_id];
      const categoryName = categoryNameById[category_id];
      if (!ratingName || !categoryName) return;

      if (!map[ratingName]) map[ratingName] = [];
      if (!map[ratingName].includes(categoryName)) map[ratingName].push(categoryName);
    });

    // 🔑 Alphabetise every category array
    Object.keys(map).forEach(key =>
      map[key].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    );

    return map;   // { [ratingName]: [sortedCategoryNames] }
  }, [ratings, categories, licenses]);

  return (
    <div>
      {loading === true && <LinearProgress />}
      < Tabs
        tabNames={userType === 'view' ? ["All Certificates"] :
          (certificatesLicenseTableView ? ["All Certificates", "Waiting for Approval", "History", "Edit properties", "Edit Companies", "Edit Subcontracts", "Licenses", "Skill Management"] :
            ["All Certificates", "Waiting for Approval", "History", "Edit properties", "Edit Companies", "Edit Subcontracts"]
          )
        }
        onChangeTabValue={(tabValue: number) => changeTab(tabValue)}
        tabValue={tabValue}
      />
      {
        userType === "view" ?
          <div className="w-full">
            {tabValue === 0
              && <AllCertificates
                    licenses={licenses}
                    userType={userType}
                    setLoading={(loadingStatus: boolean) => setLoading(loadingStatus)}
                    ratings={ratings}
                    categories={categories}
                    catByRatingString={catByRatingString}
                  />
            }
          </div>
          :
          <div className="w-full">
            {tabValue === 0 &&
              <AllCertificates
                licenses={licenses}
                userType={userType}
                setLoading={(loadingStatus: boolean) => setLoading(loadingStatus)}
                ratings={ratings}
                categories={categories}
                catByRatingString={catByRatingString}
              />
            }
            {tabValue === 1 && userType === 'CertAdmin' && <WaitingForApproval setLoading={(loadingStatus: boolean) => setLoading(loadingStatus)} />}
            {tabValue === 2 && userType === 'CertAdmin' && <History loading={loading} setLoading={(loadingStatus: boolean) => setLoading(loadingStatus)} />}
            {tabValue === 3 && userType === 'CertAdmin' && <EditProperties setLoading={(loadingStatus: boolean) => setLoading(loadingStatus)} />}
            {tabValue === 4 && userType === 'CertAdmin' && <EditCompanies setLoading={(loadingStatus: boolean) => setLoading(loadingStatus)} />}
            {tabValue === 5 && userType === 'CertAdmin' && <EditSubcontracts setLoading={(loadingStatus: boolean) => setLoading(loadingStatus)} />}
            {tabValue === 6 &&
              certificatesLicenseTableView &&
              <LicenseTable
                setLoading={(loadingStatus: boolean) => setLoading(loadingStatus)}
                isAdmin={userGroups.includes('Licenses Edit')}
              />
            }
            {tabValue === 7 &&
              certificatesLicenseTableView &&
              <SkillManagement
                setLoading={(loadingStatus: boolean) => setLoading(loadingStatus)}
                isAdmin={userGroups.includes('Licenses Edit')}
              />
            }
          </div>
      }
      {loading === true &&
        <div></div>
      }
    </div>
  );
};
export default Certificates;